import type {
  ArchiveLibraryTypeBody,
  CreateLibraryTypeBody,
  LibraryHistory,
  LibraryPlant,
  LibraryType,
  LibraryTypeSummary,
  NodeTypeOption,
  PlantTypeProfile,
  ReadingDef,
  UpdateLibraryTypeBody,
} from '../types/contract';

import { CTL_CONFIG_ENDPOINTS } from './endpoints';
import { checkedBody, parsed, request } from './http';
import { archiveLibraryTypeBodySchema, createLibraryTypeBodySchema, updateLibraryTypeBodySchema } from './requestSchemas';
import {
  libraryHistorySchema,
  libraryListSchema,
  libraryPlantsSchema,
  libraryTypeSchema,
  nodeTypesSchema,
  plantTypesSchema,
  readingsSchema,
} from './schemas';

export const fetchLibraryList = async (signal?: AbortSignal): Promise<LibraryTypeSummary[]> => {
  const raw = await request('get', CTL_CONFIG_ENDPOINTS.TYPES, { signal });
  return parsed(libraryListSchema, raw, 'equipment library list').items;
};

export const fetchLibraryType = async (code: string, signal?: AbortSignal): Promise<LibraryType> => {
  const raw = await request('get', CTL_CONFIG_ENDPOINTS.TYPE(code), { signal });
  return parsed(libraryTypeSchema, raw, 'library type');
};

export const createLibraryType = async (body: CreateLibraryTypeBody): Promise<LibraryType> => {
  const raw = await request('post', CTL_CONFIG_ENDPOINTS.TYPES, { body: checkedBody(createLibraryTypeBodySchema, body, 'new library type') });
  return parsed(libraryTypeSchema, raw, 'created library type');
};

export const replaceLibraryType = async (code: string, body: UpdateLibraryTypeBody): Promise<LibraryType> => {
  const raw = await request('put', CTL_CONFIG_ENDPOINTS.TYPE(code), { body: checkedBody(updateLibraryTypeBodySchema, body, 'saved library type') });
  return parsed(libraryTypeSchema, raw, 'saved library type');
};

export const archiveLibraryType = async (code: string, body: ArchiveLibraryTypeBody): Promise<LibraryType> => {
  const raw = await request('delete', CTL_CONFIG_ENDPOINTS.TYPE(code), { body: checkedBody(archiveLibraryTypeBodySchema, body, 'library type archive') });
  return parsed(libraryTypeSchema, raw, 'archived library type');
};

export const fetchLibraryHistory = async (code: string, signal?: AbortSignal): Promise<LibraryHistory> => {
  const raw = await request('get', CTL_CONFIG_ENDPOINTS.TYPE_HISTORY(code), { signal });
  return parsed(libraryHistorySchema, raw, 'library type history');
};

export const fetchReadings = async (signal?: AbortSignal): Promise<ReadingDef[]> => {
  const raw = await request('get', CTL_CONFIG_ENDPOINTS.READINGS, { signal });
  return parsed(readingsSchema, raw, 'readings vocabulary').items;
};

export const fetchPlantTypes = async (signal?: AbortSignal): Promise<PlantTypeProfile[]> => {
  const raw = await request('get', CTL_CONFIG_ENDPOINTS.PLANT_TYPES, { signal });
  return parsed(plantTypesSchema, raw, 'plant types').items;
};

export const fetchLibraryPlants = async (signal?: AbortSignal): Promise<LibraryPlant[]> => {
  const raw = await request('get', CTL_CONFIG_ENDPOINTS.PLANTS, { signal });
  return parsed(libraryPlantsSchema, raw, 'library plants').items;
};

export const fetchNodeTypes = async (signal?: AbortSignal): Promise<NodeTypeOption[]> => {
  const raw = await request('get', CTL_CONFIG_ENDPOINTS.NODE_TYPES, { signal });
  return parsed(nodeTypesSchema, raw, 'equipment node types').items;
};
